class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.continueButton = this.notification?.querySelector('.cart-continue-link');

    this.continueButton?.addEventListener('click', this.close.bind(this));
    
    // Close on overlay click
    this.addEventListener('click', (event) => {
      if (event.target === this) {
        this.close();
      }
    });

    this.activeElement = null;
    
    // Listen for cart updates
    this.setupCartListeners();
  }

  open() {
    if (!this.notification) return;
    
    this.notification.style.display = 'block';
    document.body.classList.add('cart-notification-open');
    
    // Trap focus
    this.notification.focus();
  }

  close() {
    if (!this.notification) return;
    
    this.notification.style.display = 'none';
    document.body.classList.remove('cart-notification-open');
    
    if (this.activeElement) {
      this.activeElement.focus();
    }
  }

  setupCartListeners() {
    // Listen for cart updates from cart page and other components
    document.addEventListener('cart:updated', (event) => {
      if (event.detail && event.detail.cart) {
        this.updateCartHTML(event.detail.cart);
        this.open(); // Re-open with updated content
      }
    });
    
    // Also listen for the custom event that cart.js fires
    document.addEventListener('cart:change', (event) => {
      this.fetchAndUpdateCart();
    });
    
    // Listen for quantity changes from cart page
    document.addEventListener('quantity:change', () => {
      this.fetchAndUpdateCart();
    });
  }

  fetchAndUpdateCart() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        this.updateCartHTML(cart);
        // Dispatch event for other components
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      })
      .catch(error => {
        console.error('Error fetching cart:', error);
      });
  }

  updateCartHTML(cart) {
    if (!this.notification) return;

    const itemCount = cart.item_count;
    
    if (itemCount > 0) {
      // Calculate entries
      const entriesEnabled = window.cartSettings?.enable_enteries || false;
      const entriesMultiplier = window.cartSettings?.entries_value || 1;
      const maxEntriesLimit = window.cartSettings?.max_entries_limit || 0;
      
      let totalEntries = Math.round((cart.total_price / 100) * entriesMultiplier);
      if (maxEntriesLimit > 0 && totalEntries > maxEntriesLimit) {
        totalEntries = maxEntriesLimit;
      }

      // Build promo banner HTML
      let promoBanner = '';
      if (entriesEnabled) {
        promoBanner = `
          <div class="cart-promo-banner">
            <div class="promo-content">
              <div class="promo-icon">
                <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 33.948 33.948"><path d="M20.447-45l6.366,6.366L15.289-27.11,8.922-33.476ZM16.2-25.277,28.646-37.727a1.239,1.239,0,0,0,.383-.907,1.239,1.239,0,0,0-.383-.907l-7.293-7.293a1.23,1.23,0,0,0-.907-.363,1.23,1.23,0,0,0-.907.363L7.089-34.383a1.239,1.239,0,0,0-.383.907,1.239,1.239,0,0,0,.383.907l7.293,7.293a1.23,1.23,0,0,0,.907.363A1.23,1.23,0,0,0,16.2-25.277ZM34.106-38.11,15.833-19.817a2.48,2.48,0,0,1-1.823.745,2.48,2.48,0,0,1-1.823-.745L9.648-22.355a3.73,3.73,0,0,0,1.128-2.74,3.73,3.73,0,0,0-1.128-2.74,3.73,3.73,0,0,0-2.74-1.128,3.73,3.73,0,0,0-2.74,1.128L1.649-30.374A2.48,2.48,0,0,1,.9-32.2a2.48,2.48,0,0,1,.745-1.823L19.923-52.274a2.48,2.48,0,0,1,1.823-.745,2.48,2.48,0,0,1,1.823.745l2.518,2.518a3.73,3.73,0,0,0-1.128,2.74,3.73,3.73,0,0,0,1.128,2.74,3.73,3.73,0,0,0,2.74,1.128a3.73,3.73,0,0,0,2.74-1.128l2.539,2.518a2.48,2.48,0,0,1,.745,1.823A2.48,2.48,0,0,1,34.106-38.11Zm0,0" transform="translate(-0.904 53.019)" fill="#ed3237"/></svg>
              </div>
              <div class="promo-text">
                <span class="promo-buy">Buy and get</span>
                <span class="promo-entries">${totalEntries} entries</span>
              </div>
              <div class="promo-image"> 
                ${window.entriesImageHTML || ''} 
              </div>
            </div>
          </div>
        `;
      }

      // Update cart with items
      const newContent = `
        <div class="cart-notification__header-custom">
          <div class="cart-header-content">
            <h2 class="cart-notification__title">Your cart (${itemCount})</h2>
            <div class="cart-free-shipping">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 7H16V5C16 3.9 15.1 3 14 3H4C2.9 3 2 3.9 2 5V15H4C4 16.66 5.34 18 7 18C8.66 18 10 16.66 10 15H14C14 16.66 15.34 18 17 18C18.66 18 20 16.66 20 15H22V11L19 7ZM7 16C6.45 16 6 15.55 6 15C6 14.45 6.45 14 7 14C7.55 14 8 14.45 8 15C8 15.55 7.55 16 7 16ZM18.5 8.5L20.46 11H16V8.5H18.5ZM17 16C16.45 16 16 15.55 16 15C16 14.45 16.45 14 17 14C17.55 14 18 14.45 18 15C18 15.55 17.55 16 17 16Z" fill="#0A8A3C"/>
              </svg>
              <span class="free-shipping-text">You have free shipping!</span>
            </div>
          </div>
        </div>

        <div class="cart-notification__items">
          ${cart.items.map(item => this.renderCartItem(item)).join('')}
        </div>

        ${promoBanner}

        <div class="cart-notification__actions">
          <a href="${window.routes?.cart_url || '/cart'}" class="cart-continue-link">
            Continue Shopping
          </a>
          <form action="${window.routes?.cart_url || '/cart'}" method="post" id="cart-notification-form">
            <button class="cart-checkout-button button" name="checkout">
              Checkout →
            </button>
          </form>
        </div>
      `;

      this.notification.innerHTML = newContent;
    } else {
      // Show empty cart
      const emptyContent = `
        <div class="cart-notification__header-custom">
          <div class="cart-header-content">
            <h2 class="cart-notification__title">Your cart (0)</h2>
            <div class="cart-free-shipping">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 7H16V5C16 3.9 15.1 3 14 3H4C2.9 3 2 3.9 2 5V15H4C4 16.66 5.34 18 7 18C8.66 18 10 16.66 10 15H14C14 16.66 15.34 18 17 18C18.66 18 20 16.66 20 15H22V11L19 7ZM7 16C6.45 16 6 15.55 6 15C6 14.45 6.45 14 7 14C7.55 14 8 14.45 8 15C8 15.55 7.55 16 7 16ZM18.5 8.5L20.46 11H16V8.5H18.5ZM17 16C16.45 16 16 15.55 16 15C16 14.45 16.45 14 17 14C17.55 14 18 14.45 18 15C18 15.55 17.55 16 17 16Z" fill="#0A8A3C"/>
              </svg>
              <span class="free-shipping-text">You have free shipping!</span>
            </div>
          </div>
        </div>

        <div class="cart-empty-state">
          <h3 class="cart-empty-title">No products in the cart.</h3>
          <p class="cart-empty-subtitle">Hey, we have something for you...</p>
          <a href="/collections/top-sellers" class="cart-explore-button button">
            Explore Our TOP Sellers →
          </a>
        </div>
      `;

      this.notification.innerHTML = emptyContent;
    }

    // Re-attach event listeners
    this.continueButton = this.notification.querySelector('.cart-continue-link');
    if (this.continueButton) {
      this.continueButton.addEventListener('click', this.close.bind(this));
    }
  }

  renderCartItem(item) {
    const itemPrice = this.formatMoney(item.final_line_price);
    const imageUrl = item.image ? item.image.replace(/(\.\w{3,4})(\?|$)/, '_120x120$1$2') : '';
    const variantTitle = item.variant_title !== 'Default Title' ? item.variant_title : '';

    return `
      <div class="cart-notification__item">
        <div class="cart-item-image">
          ${imageUrl ? `<img src="${imageUrl}" alt="${this.escapeHtml(item.product_title)}" loading="lazy">` : ''}
        </div>
        <div class="cart-item-details">
          <h3 class="cart-item-title">${this.escapeHtml(item.product_title)}</h3>
          ${variantTitle ? `<p class="cart-item-variant">Size: ${this.escapeHtml(variantTitle)}</p>` : ''}
        </div>
        <div class="cart-item-quantity cart-item-price">
          <span class="quantity-label">${item.quantity} pcs</span>
          <span class="price">${itemPrice}</span>
        </div>
      </div>
    `;
  }

  formatMoney(cents) {
    const dollars = cents / 100;
    return `$${dollars.toFixed(2)}`;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);